import { useState } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import "../../../assets/css/doctor/doctorpages/TherapistAnalytics.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";

const TherapistAnalytics = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('all');

  // Session Analytics Data
  const sessionTrendsData = [
    { month: 'Jan', completed: 85, cancelled: 8, noShow: 7 },
    { month: 'Feb', completed: 92, cancelled: 6, noShow: 2 },
    { month: 'Mar', completed: 88, cancelled: 9, noShow: 3 },
    { month: 'Apr', completed: 95, cancelled: 3, noShow: 2 },
    { month: 'May', completed: 98, cancelled: 2, noShow: 0 },
    { month: 'Jun', completed: 102, cancelled: 5, noShow: 3 },
  ];

  // Patient Demographics
  const demographicsData = [
    { name: '18-25', value: 28, color: '#FF6B35' },
    { name: '26-35', value: 35, color: '#FF8C61' },
    { name: '36-45', value: 22, color: '#0A1628' },
    { name: '46-55', value: 10, color: '#1A2942' },
    { name: '56+', value: 5, color: '#2A3952' },
  ];

  // Treatment Outcomes
  const outcomesData = [
    { week: 'Week 1', improvement: 45, stable: 40, declined: 15 },
    { week: 'Week 2', improvement: 52, stable: 38, declined: 10 },
    { week: 'Week 3', improvement: 58, stable: 35, declined: 7 },
    { week: 'Week 4', improvement: 65, stable: 30, declined: 5 },
    { week: 'Week 5', improvement: 72, stable: 25, declined: 3 },
    { week: 'Week 6', improvement: 78, stable: 20, declined: 2 },
  ];

  // Session Duration Analytics
  const durationData = [
    { day: 'Mon', avg: 52 },
    { day: 'Tue', avg: 48 },
    { day: 'Wed', avg: 55 },
    { day: 'Thu', avg: 50 },
    { day: 'Fri', avg: 53 },
    { day: 'Sat', avg: 45 },
    { day: 'Sun', avg: 40 },
  ];

  // Revenue Analytics
  const revenueData = [
    { month: 'Jan', revenue: 45000, target: 50000 },
    { month: 'Feb', revenue: 52000, target: 50000 },
    { month: 'Mar', revenue: 48000, target: 50000 },
    { month: 'Apr', revenue: 58000, target: 55000 },
    { month: 'May', revenue: 62000, target: 55000 },
    { month: 'Jun', revenue: 68000, target: 60000 },
  ];

  // Satisfaction Scores
  const satisfactionData = [
    { category: 'Communication', score: 9.2 },
    { category: 'Professionalism', score: 9.5 },
    { category: 'Effectiveness', score: 8.8 },
    { category: 'Environment', score: 9.0 },
    { category: 'Scheduling', score: 8.5 },
  ];

  const kpiData = [
    {
      title: 'Total Sessions',
      value: '1,247',
      change: '+12.5%',
      trend: 'up',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    },
    {
      title: 'Active Patients',
      value: '156',
      change: '+8.3%',
      trend: 'up',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )
    },
    {
      title: 'Success Rate',
      value: '94.2%',
      change: '+3.1%',
      trend: 'up',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      )
    },
    {
      title: 'Avg Satisfaction',
      value: '9.0/10',
      change: '+0.5',
      trend: 'up',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      )
    },
  ];

  return (
    <DoctorMasterLayout>
    <div className="analytics-wrapper">
      <div className="analytics-container">
        {/* Header Section */}
        <div className="analytics-header">
          <div className="header-content">
            <h1 className="page-title">Therapist Analytics Dashboard</h1>
            <p className="page-subtitle">Comprehensive insights into your therapy practice performance</p>
          </div>
          <div className="header-actions">
            <select 
              className="time-range-select"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
            <button className="export-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export Report
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-grid">
          {kpiData.map((kpi, index) => (
            <div key={index} className="kpi-card">
              <div className="kpi-icon-wrapper">
                <div className="kpi-icon">{kpi.icon}</div>
              </div>
              <div className="kpi-content">
                <div className="kpi-label">{kpi.title}</div>
                <div className="kpi-value">{kpi.value}</div>
                <div className={`kpi-change ${kpi.trend}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                  {kpi.change}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Charts Grid */}
        <div className="charts-main-grid">
          {/* Session Trends */}
          <div className="chart-card chart-card-large">
            <div className="chart-header">
              <div className="chart-header-left">
                <svg className="chart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="20" x2="12" y2="10" />
                  <line x1="18" y1="20" x2="18" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="16" />
                </svg>
                <h3 className="chart-title">Session Trends Analysis</h3>
              </div>
              <div className="chart-legend-custom">
                <span className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: '#FF6B35' }}></span>
                  Completed
                </span>
                <span className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: '#0A1628' }}></span>
                  Cancelled
                </span>
                <span className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: '#E5E7EB' }}></span>
                  No Show
                </span>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={sessionTrendsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Area type="monotone" dataKey="completed" stackId="1" stroke="#FF6B35" fill="#FF6B35" fillOpacity={0.8} />
                  <Area type="monotone" dataKey="cancelled" stackId="1" stroke="#0A1628" fill="#0A1628" fillOpacity={0.8} />
                  <Area type="monotone" dataKey="noShow" stackId="1" stroke="#E5E7EB" fill="#E5E7EB" fillOpacity={0.8} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Demographics Pie Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-header-left">
                <svg className="chart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="chart-title">Patient Demographics</h3>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={demographicsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {demographicsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Secondary Charts Grid */}
        <div className="charts-secondary-grid">
          {/* Treatment Outcomes */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-header-left">
                <svg className="chart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                <h3 className="chart-title">Treatment Outcomes</h3>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={outcomesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="week" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="improvement" fill="#10B981" name="Improved" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="stable" fill="#FBBF24" name="Stable" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="declined" fill="#EF4444" name="Declined" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Session Duration */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-header-left">
                <svg className="chart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <h3 className="chart-title">Average Session Duration</h3>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={durationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="day" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avg" 
                    stroke="#FF6B35" 
                    strokeWidth={3}
                    dot={{ fill: '#FF6B35', r: 5 }}
                    name="Avg Duration (min)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Bottom Grid */}
        <div className="bottom-analytics-grid">
          {/* Revenue Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-header-left">
                <svg className="chart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                <h3 className="chart-title">Revenue vs Target</h3>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#FF6B35" name="Revenue" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="target" fill="#0A1628" name="Target" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Satisfaction Scores */}
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-header-left">
                <svg className="chart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <h3 className="chart-title">Satisfaction Breakdown</h3>
              </div>
            </div>
            <div className="chart-body">
              <div className="satisfaction-list">
                {satisfactionData.map((item, index) => (
                  <div key={index} className="satisfaction-item">
                    <div className="satisfaction-label">{item.category}</div>
                    <div className="satisfaction-bar-wrapper">
                      <div className="satisfaction-bar">
                        <div 
                          className="satisfaction-fill"
                          style={{ width: `${(item.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <div className="satisfaction-score">{item.score}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Insights Section */}
        <div className="insights-section">
          <div className="insights-header">
            <svg className="insights-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
            <h3 className="insights-title">Key Insights & Recommendations</h3>
          </div>
          <div className="insights-grid">
            <div className="insight-card insight-success">
              <div className="insight-icon-wrapper">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="insight-content">
                <div className="insight-title">High Success Rate</div>
                <div className="insight-text">Your treatment success rate is 94.2%, exceeding the industry average of 85%</div>
              </div>
            </div>

            <div className="insight-card insight-warning">
              <div className="insight-icon-wrapper">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="insight-content">
                <div className="insight-title">Peak Hours Identified</div>
                <div className="insight-text">Consider scheduling more sessions on Thursdays when demand is highest</div>
              </div>
            </div>

            <div className="insight-card insight-info">
              <div className="insight-icon-wrapper">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <div className="insight-content">
                <div className="insight-title">Revenue Growth</div>
                <div className="insight-text">Monthly revenue increased by 15% compared to last quarter</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </DoctorMasterLayout>
  );
};

export default TherapistAnalytics;